import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createEditor, Editor, Transforms, Text, Range } from "slate";
import { Slate, Editable, withReact, ReactEditor } from "slate-react";
import { withHistory } from "slate-history";
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaLink,
  FaListUl,
  FaListOl,
  FaQuoteLeft,
  FaCode,
  FaImage,
  FaUpload,
} from "react-icons/fa";
import XIcon from '@mui/icons-material/X';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkIcon from '@mui/icons-material/Link';
import SendIcon from '@mui/icons-material/Send';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { MdDelete, MdTitle } from "react-icons/md";
import { FaArrowRight } from "react-icons/fa6";
import { HiChevronDown } from "react-icons/hi";
import { MdOutlineModeEdit } from "react-icons/md";
import { MdOutlineArrowDropDown } from "react-icons/md";
const SHORTCUTS = {
  "*": "list-item",
  "-": "list-item",
  "+": "list-item",
  "1.": "list-item",
  ">": "block-quote",
  "#": "heading-one",
  "##": "heading-two",
  "`": "code",
};

const withLinks = (editor) => {
  const { insertData, insertText, isInline } = editor;

  editor.isInline = (element) => {
    return element.type === "link" ? true : isInline(element);
  };

  editor.insertText = (text) => {
    if (text && isUrl(text)) {
      wrapLink(editor, text);
    } else {
      insertText(text);
    }
  };

  editor.insertData = (data) => {
    const text = data.getData("text/plain");
    if (text && isUrl(text)) {
      wrapLink(editor, text);
    } else {
      insertData(data);
    }
  };

  return editor;
};

const isUrl = (text) => {
  const urlPattern = /^(https?:\/\/)?([\w\d-]+\.)+[\w\d]{2,}(\/.*)?$/i;
  return urlPattern.test(text);
};

const LinkModal = ({ isOpen, onClose, onSubmit }) => {
  const [url, setUrl] = useState("");

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (url.trim()) {
      onSubmit(url);
      setUrl("");
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-4 rounded-lg shadow-lg w-96">
        <h3 className="text-lg font-semibold mb-2">Insert Link</h3>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Enter URL (e.g., https://example.com)"
            className="w-full p-2 border rounded mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1 text-gray-600 rounded hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Insert
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const LinkedInEditor = () => {
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [images, setImages] = useState("");
  const dropAreaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [title, setTitle] = useState("");
  const [isHovered, setIsHovered] = useState(false);
  const [showShareDropdown, setShowShareDropdown] = useState(false);
  const shareDropdownRef = useRef(null);
  const shareButtonRef = useRef(null);
  const [publishAs, setPublishAs] = useState("shivamMantri");
  const [publishTo, setPublishTo] = useState("individualArticle");
  const [showManageDropdown, setShowManageDropdown] = useState(false);
  const manageDropdownRef = useRef(null);
  const manageButtonRef = useRef(null);
  useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          showShareDropdown && 
          shareDropdownRef.current && 
          !shareDropdownRef.current.contains(event.target) &&
          !shareButtonRef.current.contains(event.target)
        ) {
          setShowShareDropdown(false);
        }
         if (
          showManageDropdown && 
          manageDropdownRef.current && 
          !manageDropdownRef.current.contains(event.target) &&
          !manageButtonRef.current.contains(event.target)
        ) {
          setShowManageDropdown(false);
        }
      };

      if (showShareDropdown || showManageDropdown) {
        document.addEventListener("mousedown", handleClickOutside);
      }
  
      return () => {
        
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [showShareDropdown,showManageDropdown]); 

  const [value, setValue] = useState([
    {
      type: "paragraph",
      children: [{ text: "" }],
    },
  ]);
  const editorRef = useRef(null);
  const editor = useMemo(
    () => withLinks(withHistory(withReact(createEditor()))),
    []
  );

  const renderElement = useCallback((props) => {
    switch (props.element.type) {
      case "heading-one":
        return (
          <h1 {...props.attributes} className="text-2xl font-bold mb-2">
            {props.children}
          </h1>
        );
      case "heading-two":
        return (
          <h2 {...props.attributes} className="text-xl font-bold mb-2">
            {props.children}
          </h2>
        );
      case "block-quote":
        return (
          <blockquote
            {...props.attributes}
            className="border-l-4 pl-4 italic text-gray-600"
          >
            {props.children}
          </blockquote>
        );
      case "list-item":
        return <li {...props.attributes}>{props.children}</li>;
      case "numbered-list":
        return (
          <ol {...props.attributes} className="list-decimal ml-6">
            {props.children}
          </ol>
        );
      case "bulleted-list":
        return (
          <ul {...props.attributes} className="list-disc ml-6">
            {props.children}
          </ul>
        );
      case "code":
        return (
          <pre
            {...props.attributes}
            className="bg-gray-100 p-2 rounded font-mono text-sm"
          >
            <code>{props.children}</code>
          </pre>
        );
      case "link":
        return (
          <a
            {...props.attributes}
            href={props.element.url}
            className="text-blue-600 underline"
          >
            {props.children}
          </a>
        );
      default:
        return (
          <p {...props.attributes} className="mb-2">
            {props.children}
          </p>
        );
    }
  }, []);

  const renderLeaf = useCallback((props) => {
    return (
      <span
        {...props.attributes}
        className={`
            ${props.leaf.bold ? "font-bold" : ""}
            ${props.leaf.italic ? "italic" : ""}
            ${props.leaf.underline ? "underline" : ""}
            ${props.leaf.code ? "bg-gray-100 font-mono px-1 rounded" : ""}
          `}
      >
        {props.children}
      </span>
    );
  }, []);

  const insertImage = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    handleFiles(files);
    event.target.value = null;
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    handleFiles(files);
    dropAreaRef.current.classList.remove("border-blue-500");
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    dropAreaRef.current.classList.add("border-blue-500");
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    dropAreaRef.current.classList.remove("border-blue-500");
  };

  const handleFiles = (files) => {
    const file = files[0]; 
    if (file.type.startsWith("image/")) {
      const maxSize = 5 * 1024 * 1024; 
      if (file.size > maxSize) {
        alert("Image file size should not exceed 5MB");
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setImages(e.target.result); 
      };
      reader.readAsDataURL(file);
    } else {
      alert("Please select an image file");
    }
  };

  const removeImage = () => {
    setImages(""); 
  };
  const toggleFormat = (editor, format) => {
    const isActive = isFormatActive(editor, format);
    Transforms.setNodes(
      editor,
      { [format]: isActive ? null : true },
      { match: (n) => Text.isText(n), split: true }
    );
  };

  const toggleBlock = (editor, format) => {
    const isActive = isBlockActive(editor, format);
    const isList = ["numbered-list", "bulleted-list"].includes(format);

    Transforms.unwrapNodes(editor, {
      match: (n) => ["bulleted-list", "numbered-list"].includes(n.type),
      split: true,
      mode: "all",
    });

    const newProperties = {
      type: isActive ? "paragraph" : isList ? "list-item" : format,
    };

    Transforms.setNodes(editor, newProperties);

    if (!isActive && isList) {
      const block = { type: format, children: [] };
      Transforms.wrapNodes(editor, block);
    }
  };

  const isFormatActive = (editor, format) => {
    const [match] = Editor.nodes(editor, {
      match: (n) => n[format] === true,
      mode: "all",
    });
    return !!match;
  };

  const isBlockActive = (editor, format) => {
    const [match] = Editor.nodes(editor, {
      match: (n) => n.type === format,
    });
    return !!match;
  };

  const wrapLink = (editor, url) => {
    const { selection } = editor;
    const isCollapsed = selection && Range.isCollapsed(selection);

    const link = {
      type: "link",
      url,
      children: isCollapsed ? [{ text: url }] : [],
    };

    if (isCollapsed) {
      Transforms.insertNodes(editor, link);
    } else {
      Transforms.wrapNodes(editor, link, { split: true });
      Transforms.collapse(editor, { edge: "end" });
    }
  };

  const insertLink = () => {
    setIsLinkModalOpen(true);
  };
  const handleLinkSubmit = (url) => {
    if (isUrl(url)) {
      wrapLink(editor, url);
    } else {
      wrapLink(editor, `https://${url}`);
    }
  };

  const handleKeyDown = (event) => {
    // if (
    //   event.key === "@" &&
    //   editor.selection &&
    //   Range.isCollapsed(editor.selection)
    // ) {
    //   const { selection } = editor;
    //   const [start] = Range.edges(selection);
    //   const wordBefore = Editor.before(editor, start, { unit: "word" });
    //   const before = wordBefore && Editor.before(editor, wordBefore);
    //   const beforeRange = before && Editor.range(editor, before, start);
    //   const beforeText = beforeRange && Editor.string(editor, beforeRange);
    //   const mentionMatch = beforeText && beforeText.match(/^@(\w+)$/);

    //   if (mentionMatch) {
    //     event.preventDefault();
    //     // Here you would typically show a dropdown of suggestions
    //     // For now, we'll just mark it as a mention
    //     Transforms.setNodes(
    //       editor,
    //       { mention: true },
    //       { at: selection.anchor.path, match: Text.isText }
    //     );
    //   }
    // }

    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case "b":
          event.preventDefault();
          toggleFormat(editor, "bold");
          break;
        case "i":
          event.preventDefault();
          toggleFormat(editor, "italic");
          break;
        case "u":
          event.preventDefault();
          toggleFormat(editor, "underline");
          break;
      }
    }

    if (event.key === "Enter" && isBlockActive(editor, "code")) {
      event.preventDefault();
      Transforms.insertText(editor, "\n");
      return;
    }

    if (
      event.key === " " &&
      editor.selection &&
      Range.isCollapsed(editor.selection)
    ) {
      const { anchor } = editor.selection;
      const block = Editor.above(editor, {
        match: (n) => Editor.isBlock(editor, n),
      });
      const path = block ? block[1] : [];
      const start = Editor.start(editor, path);
      const range = { anchor, focus: start };
      const beforeText = Editor.string(editor, range);
      const type = SHORTCUTS[beforeText];

      if (type) {
        event.preventDefault();
        Transforms.select(editor, range);
        Transforms.delete(editor);
        toggleBlock(
          editor,
          type === "list-item"
            ? beforeText === "1."
              ? "numbered-list"
              : "bulleted-list"
            : type
        );
      }
    }
  };
  const handleTitleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault(); 
      
      if (editorRef.current) {
        ReactEditor.focus(editor);
        
        Transforms.select(editor, {
          path: [0, 0],
          offset: 0,
        });
      }
    }
  };

  const ToolbarButton = ({ format, icon: Icon, isBlock = false, onClick }) => {
    const isActive = isBlock
      ? isBlockActive(editor, format)
      : isFormatActive(editor, format);

    const handleClick = isBlock
      ? () => toggleBlock(editor, format)
      : onClick || (() => toggleFormat(editor, format));

    return (
      <button
        className={`p-2 mx-0.5 rounded ${
          isActive ? "bg-gray-200" : "bg-transparent"
        } hover:bg-gray-200`}
        onMouseDown={(event) => {
          event.preventDefault();
          handleClick();
        }}
      >
        <Icon className="w-4 h-4" />
      </button>
    );
  };

  return (
    <div className="bg-white mt-16 relative">
      <div
        className="border-b flex items-center justify-between border-gray-200 px-4 sm:px-6 lg:px-28 fixed top-16 left-0 right-0 bg-white z-10"
        style={{ height: "84px" }}
      >
        {/* <div className="bg-white flex justify-between items-center rounded-[30px] px-4 py-2">
          <div
            onClick={() => setShowShareDropdown(!showShareDropdown)}
            ref={shareButtonRef}
            className="relative flex items-start rounded-lg p-2"
          >
            <img
              src="https://placehold.co/100x100"
              alt="User profile picture"
              className="rounded-full w-12 h-12 mr-3"
            />
            <div className="flex-grow">
              <div className="font-semibold text-lg text-black">
                Shivam Mantri
              </div>
              <div className="text-gray-500 text-[12px]">Post to Anyone</div>
            </div>
            <MdOutlineArrowDropDown className="size-6 text-gray-600" />
          </div>
          {showShareDropdown && (
            <div
              ref={shareDropdownRef}
              className="absolute bg-white border rounded-r-lg rounded-b-lg mt-56 p-4 w-72 z-50 space-y-4"
            >
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Publish as
                </label>

               
                <div className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      S
                    </div>
                    <label
                      htmlFor="shivamMantri"
                      className="text-sm text-gray-900 cursor-pointer"
                    >
                      Shivam Mantri
                    </label>
                  </div>
                  <div className="relative">
                    <input
                      type="radio"
                      name="publishAs"
                      id="shivamMantri"
                      className="peer hidden"
                      checked={publishAs === "shivamMantri"}
                      onChange={() => setPublishAs("shivamMantri")}
                    />
                    <div className="w-5 h-5 border-2 border-gray-300 rounded-full flex items-center justify-center">
                      <div
                        className={`w-3 h-3 bg-green-600 rounded-full transition-opacity ${
                          publishAs === "shivamMantri"
                            ? "opacity-100"
                            : "opacity-0"
                        }`}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

             
              <div className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-500">
                    📄
                  </div>
                  <label
                    htmlFor="individualArticle"
                    className="text-sm text-gray-900 cursor-pointer"
                  >
                    Individual article
                  </label>
                </div>
                <div className="relative">
                  <input
                    type="radio"
                    name="publishTo"
                    id="individualArticle"
                    className="peer hidden"
                    checked={publishTo === "individualArticle"}
                    onChange={() => setPublishTo("individualArticle")}
                  />
                  <div className="w-5 h-5 border-2 border-gray-300 rounded-full flex items-center justify-center">
                    <div
                      className={`w-3 h-3 bg-green-600 rounded-full transition-opacity ${
                        publishTo === "individualArticle"
                          ? "opacity-100"
                          : "opacity-0"
                      }`}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div> */}
        <div className="bg-white flex justify-between items-center rounded-[30px] px-4 py-2 relative">
          <div
            onClick={() => setShowShareDropdown(!showShareDropdown)}
            ref={shareButtonRef}
            className="relative flex items-start rounded-lg p-2 cursor-pointer hover:bg-gray-100 transition-colors w-full"
          >
            <img
              src="https://placehold.co/100x100"
              alt="User profile picture"
              className="rounded-full w-12 h-12 mr-3"
            />
            <div className="flex-grow">
              <div className="font-semibold text-lg text-black">
                Shivam Mantri
              </div>
              <div className="text-gray-500 text-[12px]">
                {publishTo === "individualArticle"
                  ? "Individual article"
                  : "Other Option"}
              </div>
            </div>
            <MdOutlineArrowDropDown
              className={`size-6 text-gray-600 transition-transform ${
                showShareDropdown ? "rotate-180" : ""
              }`}
            />
          </div>

          {showShareDropdown && (
            <div
              ref={shareDropdownRef}
              className="absolute bg-white border border-gray-200  rounded-r-lg rounded-b-lg mt-2 p-4 w-72 z-50 space-y-4 top-full left-0"
            >
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Publish as
                </label>

                <div
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={() => setPublishAs("shivamMantri")}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      S
                    </div>
                    <label
                      htmlFor="shivamMantri"
                      className="text-sm text-gray-900 cursor-pointer"
                    >
                      Shivam Mantri
                    </label>
                  </div>
                  <div className="relative">
                    <input
                      type="radio"
                      name="publishAs"
                      id="shivamMantri"
                      className="absolute opacity-0 w-0 h-0"
                      checked={publishAs === "shivamMantri"}
                      onChange={() => {}}
                    />
                    <div className="w-5 h-5 border-2 border-gray-300 rounded-full flex items-center justify-center">
                      <div
                        className={`w-3 h-3 bg-green-600 rounded-full transition-opacity ${
                          publishAs === "shivamMantri"
                            ? "opacity-100"
                            : "opacity-0"
                        }`}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 my-2"></div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Publish to
                </label>

                <div
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={() => setPublishTo("individualArticle")}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-500">
                      📄
                    </div>
                    <label
                      htmlFor="individualArticle"
                      className="text-sm text-gray-900 cursor-pointer"
                    >
                      Individual article
                    </label>
                  </div>
                  <div className="relative">
                    <input
                      type="radio"
                      name="publishTo"
                      id="individualArticle"
                      className="absolute opacity-0 w-0 h-0"
                      checked={publishTo === "individualArticle"}
                      onChange={() => {}}
                    />
                    <div className="w-5 h-5 border-2 border-gray-300 rounded-full flex items-center justify-center">
                      <div
                        className={`w-3 h-3 bg-green-600 rounded-full transition-opacity ${
                          publishTo === "individualArticle"
                            ? "opacity-100"
                            : "opacity-0"
                        }`}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Add more publish options as needed */}
                {/* <div
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={() => setPublishTo("anotherOption")}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-500">
                      👥
                    </div>
                    <label
                      htmlFor="anotherOption"
                      className="text-sm text-gray-900 cursor-pointer"
                    >
                      Another option
                    </label>
                  </div>
                  <div className="relative">
                    <input
                      type="radio"
                      name="publishTo"
                      id="anotherOption"
                      className="absolute opacity-0 w-0 h-0"
                      checked={publishTo === "anotherOption"}
                      onChange={() => {}}
                    />
                    <div className="w-5 h-5 border-2 border-gray-300 rounded-full flex items-center justify-center">
                      <div
                        className={`w-3 h-3 bg-green-600 rounded-full transition-opacity ${
                          publishTo === "anotherOption"
                            ? "opacity-100"
                            : "opacity-0"
                        }`}
                      ></div>
                    </div>
                  </div>
                </div> */}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-1 px-4 py-2 bg-gray-300 rounded-full h-12">
          <ToolbarButton format="heading-one" icon={MdTitle} isBlock />
          <ToolbarButton format="bold" icon={FaBold} />
          <ToolbarButton format="italic" icon={FaItalic} />
          <ToolbarButton format="underline" icon={FaUnderline} />
          <ToolbarButton format="link" icon={FaLink} onClick={insertLink} />
          <div className="h-6 w-px bg-gray-300 mx-1" />
          <ToolbarButton format="bulleted-list" icon={FaListUl} isBlock />
          <ToolbarButton format="numbered-list" icon={FaListOl} isBlock />
          <div className="h-6 w-px bg-gray-300 mx-1" />
          <ToolbarButton format="block-quote" icon={FaQuoteLeft} isBlock />
          <ToolbarButton format="code" icon={FaCode} isBlock />
          <div className="h-6 w-px bg-gray-300 mx-1" />
          <ToolbarButton format="image" icon={FaImage} onClick={insertImage} />
        </div>
        <div className="flex space-x-4 relative">
          {" "}
          <div className="relative">
            {" "}
            <button
              className="flex items-center space-x-1 px-3 py-1.5 border border-blue-600 text-blue-600 rounded-full hover:bg-blue-50"
              onClick={() => setShowManageDropdown(!showManageDropdown)}
              ref={manageButtonRef}
            >
              <span>Manage</span>
              <MdOutlineArrowDropDown
                className={`size-6 text-blue-600 transition-transform ${
                  showManageDropdown ? "rotate-180" : ""
                }`}
              />
            </button>
            {showManageDropdown && (
              <div
                ref={manageDropdownRef}
                className="absolute bg-white border border-gray-300 shadow-lg rounded-r-lg rounded-b-lg  z-50  mt-1 w-48 left-0"
              >
                <button
                  className="flex items-center w-full px-3 py-2 text-gray-800 text-sm font-medium hover:bg-gray-100"
                  // onClick={handleCopy}
                >
                  Settings
                </button>
                <div className="border-t border-gray-300 "></div>
                <button
                  className="flex items-center w-full px-3 py-2 text-gray-800 text-sm font-medium hover:bg-gray-100"
                  // onClick={handleDelete}
                >
                  Drafts
                </button>
                <button
                  className="flex items-center w-full px-3 py-2 text-gray-800 text-sm font-medium hover:bg-gray-100"
                  // onClick={handleDelete}
                >
                  Scheduled
                </button>
                <button
                  className="flex items-center w-full px-3 py-2 text-gray-800 text-sm font-medium hover:bg-gray-100"
                  // onClick={handleDelete}
                >
                  Published
                </button>
                <div className="border-t border-gray-300 "></div>
                <button
                  className="flex items-center w-full px-3 py-2 text-gray-800 text-sm font-medium hover:bg-gray-100"
                  // onClick={handleDelete}
                >
                  New Draft
                </button>
                <button
                  className="flex items-center w-full px-3 py-2 text-gray-800 text-sm font-medium hover:bg-gray-100"
                  // onClick={handleDelete}
                >
                  Create newsletter
                </button>
              </div>
            )}
          </div>
          <button className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white rounded-full hover:bg-blue-800">
            <span>Next</span>
            <FaArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="container mx-auto py-4 px-4 sm:px-6 lg:px-64 pt-28">
        {" "}
        {images ? (
          <div
            className="relative  mb-4 hover:shadow-lg hover:transition-shadow hover:duration-300 hover:ease-in-out cursor-pointer"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div className="absolute bg-black opacity-0 hover:opacity-50 h-full w-full rounded"></div>
            <img
              src={images}
              alt="Preview"
              className="max-w-full h-auto object-cover rounded"
            />
            {isHovered && (
              <div className="absolute top-3 right-3 flex space-x-3">
                <button
                  className="p-1 border border-white bg-transparent text-white rounded-full"
                  onClick={removeImage}
                  title="Remove"
                >
                  <MdDelete className="h-4 w-4" />
                </button>
                <button
                  className="p-1 border border-white bg-transparent text-white rounded-full"
                  onClick={insertImage}
                  title="Change"
                >
                  <MdOutlineModeEdit className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        ) : (
          <div
            ref={dropAreaRef}
            className="border-2 border-dashed border-gray-300 bg-gray-50 p-6 text-center  mb-4 flex items-center justify-center"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            style={{ height: "400px" }}
          >
            <div className="flex flex-col items-center">
              <FaImage className="mx-auto text-gray-400 mb-2" size={40} />
              <p className="text-gray-600 mb-1">
                We recommend uploading or dragging in an image that is 1920x1080
                pixels
              </p>
              <button
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-100"
                onClick={insertImage}
              >
                <FaUpload className="mr-2" /> Upload from computer
              </button>
            </div>
          </div>
        )}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Title"
            className="w-full focus:outline-none focus:none focus:none text-3xl font-medium"
            value={title}
            onKeyDown={handleTitleKeyDown}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <Slate editor={editor} initialValue={value} onChange={setValue}>
          <Editable
            ref={editorRef}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            onKeyDown={handleKeyDown}
            placeholder="Write your article here..."
            className="min-h-[400px] focus:outline-none"
          />
        </Slate>
        <div className="mt-6 flex justify-end">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
            className="hidden"
          />
          <LinkModal
            isOpen={isLinkModalOpen}
            onClose={() => setIsLinkModalOpen(false)}
            onSubmit={handleLinkSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default LinkedInEditor;
